import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useDispatch } from "react-redux";
import type { AppDispatch } from "../store/store";
import { setCurrentLocation, setLoading } from "../store/slices/appSlice";
import { useEffect } from "react";
import Layout from "../components/layout/Layout/layout";
import Home from "../pages/Home/home";
import VisitorTypes from "../pages/VisitorTypes/visitorTypes";
import VisitorForm from "../pages/VisitorForm/visitorForm";
import CapturePicture from "../pages/CapturePicture/capturePicture";
import SignDocument from "../pages/SignDocument/signDocument";
import Confirmation from "../pages/Confirmation/confirmation";
import Touchless from "../pages/Touchless/touchless";
import FullName from "../pages/FullName";
import PreRegConfirmation from "../pages/Pre-Reg-Confirmation/preRegistrationConfirmation";
import VisitorPicture from "../pages/VisitorPicture/visitorPicture";
import VisitorVideo from "../pages/VisitorVideo/visitorVideo";
import FrontBackPicture from "../pages/FrontBackPhoto/FrontBackPicture";
import Preregister from "../pages/PreRegister/preregister";
import PrivacyPolicy from "../pages/PrivacyPolicy/privacyPolicy";
import PrefillDetails from "../pages/PrefillDetails/prefillDetails";
import { ROUTES } from "../utils/constants";
import SelectHost from "../pages/SelectHost/SelectHost";
import SignOut from "../pages/SignOut/SignOut";
import SmartPassDownload from "../pages/SmartPassDownload/SmartPassDownload";
import SmartPassRedirect from "../pages/SmartPassRedirect/SmartPassRedirect";
import { FlowStepGuard } from "./FlowStepGuard";

export const AppRouter = () => {
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    // Defense-in-depth: clear any isLoading=true that was persisted to
    // sessionStorage from a previous session (e.g. page refreshed mid-request).
    // The primary fix is the redux-persist blacklist in store.ts, but this
    // guarantees Layout's full-screen spinner never starts stuck.
    dispatch(setLoading(false));

    // Initialize app - equivalent to Angular's APP_INITIALIZER
    const initializeApp = async () => {
      try {
        // Load environment settings
        const response = await fetch("/assets/env.json");
        const env = await response.json();
        dispatch(setCurrentLocation(env));
      } catch (error) {
        console.error("Failed to load environment:", error);
      }
    };

    initializeApp();
  }, [dispatch]);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route index element={<Home />} />
          <Route path={ROUTES.HOME} element={<Home />} />
          <Route path={ROUTES.VISITOR_TYPES} element={<FlowStepGuard><VisitorTypes /></FlowStepGuard>} />
          <Route
            path={ROUTES.PRE_REG_CONFIRMATION}
            element={<FlowStepGuard><PreRegConfirmation /></FlowStepGuard>}
          />
          <Route path={ROUTES.SELECT_HOST} element={<FlowStepGuard><SelectHost /></FlowStepGuard>} />
          <Route path={ROUTES.VISITOR_FORM} element={<FlowStepGuard><VisitorForm /></FlowStepGuard>} />
          <Route path={ROUTES.CAPTURE_PHOTO} element={<FlowStepGuard><CapturePicture /></FlowStepGuard>} />
          <Route path={ROUTES.SIGN_DOCUMENT} element={<FlowStepGuard><SignDocument /></FlowStepGuard>} />
          <Route path={ROUTES.CONFIRMATION} element={<Confirmation />} />
          <Route path={ROUTES.TOUCHLESS} element={<Touchless />} />
          <Route path={ROUTES.FULL_NAME} element={<FlowStepGuard><FullName /></FlowStepGuard>} />
          <Route path={ROUTES.VISITOR_PICTURE} element={<FlowStepGuard><VisitorPicture /></FlowStepGuard>} />
          <Route path={ROUTES.VISITOR_VIDEO} element={<FlowStepGuard><VisitorVideo /></FlowStepGuard>} />
          <Route
            path={ROUTES.FRONT_BACK_PHOTO}
            element={<FlowStepGuard><FrontBackPicture /></FlowStepGuard>}
          />
          <Route path={ROUTES.PRE_REGISTER} element={<Preregister />} />
          <Route path={ROUTES.PRE_FILL} element={<Preregister />} />
          <Route path={ROUTES.PRE_FILL_DETAILS} element={<FlowStepGuard><PrefillDetails /></FlowStepGuard>} />
          <Route path={ROUTES.PRIVACY_POLICY} element={<FlowStepGuard><PrivacyPolicy /></FlowStepGuard>} />
          <Route path={ROUTES.SIGN_OUT} element={<SignOut />} />
        </Route>
        {/* SmartPass — primary entry point for check-in/out via base64(email:orgId) */}
        <Route path="sp/:token" element={<SmartPassRedirect />} />
        {/* SmartPass download — standalone, no Layout wrapper */}
        <Route path="pass/:uuid" element={<SmartPassDownload />} />
        <Route path="*" element={<div>404 - Not Found</div>} />
      </Routes>
    </BrowserRouter>
  );
};
